import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useDispatch } from "react-redux";
import { setDialog } from "../../features/dialog/dialogSlice";
import { useGetBankingAccountsQuery } from "../../features/banking/bankingApiSlice";
import { useExportSepaRegistrationsMutation } from "../../features/eventRegistrations/eventRegistrationsApiSlice";
import { buildPain008Xml } from "../sepa/SepaUtils";

const toId = (value) => String(value?._id || value?.id || value || "");

const formatCurrency = (value) =>
  new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
  }).format(Number(value) || 0);

const maskIban = (iban = "") => {
  const compact = String(iban || "").replace(/\s+/g, "");
  if (!compact) return "—";
  if (compact.length <= 8) return compact;
  return `${compact.slice(0, 4)} •••• •••• ${compact.slice(-4)}`;
};

const buildDefaultReferenceText = (event = {}) => {
  const eventName = String(event?.eventName || "").trim();
  const dateLabel = event?.eventDateBegin
    ? dayjs(event.eventDateBegin).format("DD.MM.YYYY")
    : "";
  return ["Eventgebühr", eventName, dateLabel].filter(Boolean).join(" ").slice(0, 140);
};

const getSkippedCount = (previewData = {}, reason) =>
  Number(previewData?.skippedSummary?.[reason] || 0);

export default function EventRegistrationsSepaDialog({
  open,
  onClose,
  event,
  selectedRegistrationIds,
  onExported,
}) {
  const dispatch = useDispatch();
  const eventId = toId(event);
  const selectedIds = useMemo(
    () =>
      Array.from(
        new Set(
          (Array.isArray(selectedRegistrationIds) ? selectedRegistrationIds : [])
            .map((id) => String(id || "").trim())
            .filter(Boolean)
        )
      ),
    [selectedRegistrationIds]
  );
  const selectedIdsSignature = selectedIds.join("|");

  const { data: accounts = [], isLoading: loadingAccounts } =
    useGetBankingAccountsQuery(undefined, { skip: !open });
  const [previewSepa, { isLoading: isPreviewing }] =
    useExportSepaRegistrationsMutation();
  const [exportSepa, { isLoading: isExporting }] =
    useExportSepaRegistrationsMutation();

  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [referenceText, setReferenceText] = useState("");
  const [collectionDate, setCollectionDate] = useState(
    dayjs().add(2, "day").format("YYYY-MM-DD")
  );
  const [previewData, setPreviewData] = useState(null);
  const [previewErrorText, setPreviewErrorText] = useState("");

  useEffect(() => {
    if (!open) return;
    const mainAccount = accounts.find((account) => account?.isMain);
    const fallback = toId(mainAccount || accounts[0] || "");
    if (!fallback) return;
    setSelectedAccountId((prev) => {
      if (prev && accounts.some((account) => toId(account) === prev)) {
        return prev;
      }
      return fallback;
    });
  }, [open, accounts]);

  useEffect(() => {
    if (!open) return;
    setReferenceText((prev) => prev || buildDefaultReferenceText(event));
  }, [open, event]);

  useEffect(() => {
    if (!open) {
      setPreviewData(null);
      setPreviewErrorText("");
      setCollectionDate(dayjs().add(2, "day").format("YYYY-MM-DD"));
    }
  }, [open]);

  const loadPreview = useCallback(async () => {
    if (!eventId || !selectedAccountId) return;
    try {
      setPreviewErrorText("");
      const result = await previewSepa({
        eventId,
        registrationIds: selectedIds,
        referenceText: referenceText || undefined,
        bankingAccountId: selectedAccountId,
        dryRun: true,
      }).unwrap();
      setPreviewData(result);
    } catch (error) {
      const message =
        error?.data?.message ||
        "Die SEPA-Vorschau konnte nicht geladen werden.";
      setPreviewErrorText(message);
      if (error?.data && typeof error.data === "object") {
        setPreviewData({
          ...error.data,
          payments: Array.isArray(error.data.payments) ? error.data.payments : [],
          skipped: Array.isArray(error.data.skipped) ? error.data.skipped : [],
          skippedSummary:
            error.data.skippedSummary && typeof error.data.skippedSummary === "object"
              ? error.data.skippedSummary
              : {},
          exportableCount: Number(error.data.exportableCount || 0),
          registrationsTotal: Number(error.data.registrationsTotal || 0),
        });
      } else {
        setPreviewData(null);
      }
    }
  }, [eventId, previewSepa, referenceText, selectedAccountId, selectedIds]);

  useEffect(() => {
    if (!open || !eventId || !selectedAccountId) return;
    const timeoutId = setTimeout(() => {
      loadPreview();
    }, 250);
    return () => clearTimeout(timeoutId);
  }, [
    open,
    eventId,
    selectedAccountId,
    selectedIdsSignature,
    referenceText,
    loadPreview,
  ]);

  const exportableCount =
    Number(previewData?.exportableCount || previewData?.payments?.length || 0) || 0;
  const totalAmount = useMemo(
    () =>
      (Array.isArray(previewData?.payments) ? previewData.payments : []).reduce(
        (sum, payment) => sum + (Number(payment?.amount) || 0),
        0
      ),
    [previewData]
  );

  const handleExport = async () => {
    if (!eventId || !selectedAccountId) return;

    try {
      const result = await exportSepa({
        eventId,
        registrationIds: selectedIds,
        referenceText: referenceText || undefined,
        bankingAccountId: selectedAccountId,
        dryRun: false,
      }).unwrap();

      const xml = buildPain008Xml({
        creditor: result?.creditor,
        collectionDate,
        payments: (result?.payments || []).map((payment) => ({
          debtorName: payment?.debtorName || "",
          iban: payment?.iban || "",
          bic: payment?.bic || "",
          amount: Number(payment?.amount || 0),
          mandateId: payment?.mandateId || "",
          mandateDate: payment?.mandateDate || dayjs().format("YYYY-MM-DD"),
          remittance: payment?.remittance || "",
        })),
      });

      if (!xml) {
        return;
      }

      const blob = new Blob([xml], { type: "text/xml;charset=utf-8" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `SEPA_EVENT_${dayjs().format("YYYYMMDD-HHmmss")}.xml`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);

      dispatch(
        setDialog({
          type: "SUCCESS",
          title: "SEPA-Datei erzeugt",
          data: {
            successText: `${result?.registrationsUpdated || 0} Buchung(en) wurden auf „SEPA exportiert“ gesetzt. Export-ID: ${result?.exportId || "—"}`,
          },
        })
      );

      onExported?.(result);
      onClose?.();
    } catch (error) {
      dispatch(
        setDialog({
          type: "ERROR",
          title: "SEPA-Export fehlgeschlagen",
          data: {
            errorText:
              error?.data?.message ||
              "Die SEPA-Datei konnte nicht erzeugt werden.",
          },
        })
      );
    }
  };

  const notEligibleCount = getSkippedCount(previewData, "not_sepa_eligible");
  const invalidMandateCount = getSkippedCount(previewData, "invalid_mandate");
  const invalidContractCount = getSkippedCount(previewData, "invalid_contract_bank");
  const missingContractRefCount = getSkippedCount(
    previewData,
    "missing_contract_reference"
  );
  const amountNotDueCount = getSkippedCount(previewData, "amount_not_due");
  const contractNotFoundCount = getSkippedCount(previewData, "contract_not_found");

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle sx={{ pb: 1 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          justifyContent="space-between"
          alignItems={{ sm: "center" }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Icon icon="mdi:bank-transfer" width={22} />
            <Typography variant="h6">SEPA-Lastschriftexport</Typography>
          </Stack>
          <Chip
            color={selectedIds.length > 0 ? "warning" : "info"}
            label={
              selectedIds.length > 0
                ? `Auswahl: ${selectedIds.length} Buchung(en)`
                : "Umfang: Alle passenden Buchungen"
            }
          />
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {(loadingAccounts || isPreviewing) && <LinearProgress sx={{ mb: 2 }} />}

        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            background:
              "linear-gradient(135deg, rgba(25,118,210,0.10) 0%, rgba(16,185,129,0.08) 100%)",
            mb: 2,
          }}
        >
          <Stack spacing={1}>
            <Typography variant="subtitle1" fontWeight={700}>
              {event?.eventName || "Veranstaltung"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Es werden nur bestätigte Buchungen mit offenem Zahlungsstatus berücksichtigt.
              Zusätzlich müssen verwertbare Kontodaten vorliegen (SEPA-Mandat oder interne
              Anmeldung mit Vertragsdaten).
            </Typography>
          </Stack>
        </Box>

        {!eventId && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Keine Veranstaltung ausgewählt.
          </Alert>
        )}

        {!loadingAccounts && accounts.length === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Kein Bankkonto vorhanden. Bitte zuerst ein Konto in den Banking-Einstellungen
            hinterlegen.
          </Alert>
        )}

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          sx={{ mb: 2 }}
        >
          <FormControl fullWidth>
            <InputLabel id="event-sepa-account-label">
              Konto für Lastschrift
            </InputLabel>
            <Select
              labelId="event-sepa-account-label"
              label="Konto für Lastschrift"
              value={selectedAccountId}
              onChange={(eventValue) => setSelectedAccountId(eventValue.target.value)}
            >
              {accounts.map((account) => {
                const accountId = toId(account);
                return (
                  <MenuItem key={accountId} value={accountId}>
                    {account?.creditorName || "—"} ({account?.creditorIBAN || "—"})
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <TextField
            label="Fälligkeitsdatum"
            type="date"
            value={collectionDate}
            onChange={(eventValue) => setCollectionDate(eventValue.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: { md: 200 } }}
          />
        </Stack>

        <TextField
          fullWidth
          label="Verwendungszweck"
          placeholder="z. B. Eventgebühr Frühjahrscamp"
          value={referenceText}
          onChange={(eventValue) => setReferenceText(eventValue.target.value)}
          sx={{ mb: 2 }}
        />

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 2 }}>
          <Chip label={`Exportierbar: ${exportableCount}`} color="success" />
          <Chip label={`Gesamtbetrag: ${formatCurrency(totalAmount)}`} color="primary" />
          <Chip
            label={`Ausgefiltert: ${Number(previewData?.skipped?.length || 0)}`}
            color="warning"
          />
          <Button
            variant="outlined"
            startIcon={<Icon icon="mdi:refresh" />}
            onClick={loadPreview}
            disabled={!selectedAccountId || isPreviewing}
          >
            Vorschau aktualisieren
          </Button>
        </Stack>

        {!!previewErrorText && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {previewErrorText}
          </Alert>
        )}

        {(notEligibleCount > 0 ||
          invalidMandateCount > 0 ||
          invalidContractCount > 0 ||
          missingContractRefCount > 0 ||
          amountNotDueCount > 0 ||
          contractNotFoundCount > 0) && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Ausgeschlossene Buchungen:
            {notEligibleCount > 0 ? ` Keine SEPA-Eignung (${notEligibleCount})` : ""}
            {invalidMandateCount > 0
              ? `${notEligibleCount > 0 ? "," : ""} Ungültiges Mandat (${invalidMandateCount})`
              : ""}
            {invalidContractCount > 0
              ? `${
                  notEligibleCount > 0 || invalidMandateCount > 0 ? "," : ""
                } Ungültige Vertragsbankdaten (${invalidContractCount})`
              : ""}
            {missingContractRefCount > 0
              ? `${
                  notEligibleCount > 0 ||
                  invalidMandateCount > 0 ||
                  invalidContractCount > 0
                    ? ","
                    : ""
                } Fehlende Vertragsreferenz (${missingContractRefCount})`
              : ""}
            {contractNotFoundCount > 0
              ? `${
                  notEligibleCount > 0 ||
                  invalidMandateCount > 0 ||
                  invalidContractCount > 0 ||
                  missingContractRefCount > 0
                    ? ","
                    : ""
                } Vertrag nicht gefunden (${contractNotFoundCount})`
              : ""}
            {amountNotDueCount > 0
              ? `${
                  notEligibleCount > 0 ||
                  invalidMandateCount > 0 ||
                  invalidContractCount > 0 ||
                  missingContractRefCount > 0 ||
                  contractNotFoundCount > 0
                    ? ","
                    : ""
                } Kein offener Betrag (${amountNotDueCount})`
              : ""}
            .
          </Alert>
        )}

        <TableContainer
          sx={{
            maxHeight: 360,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1.5,
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Buchung</TableCell>
                <TableCell>Quelle</TableCell>
                <TableCell>Debitor</TableCell>
                <TableCell>IBAN</TableCell>
                <TableCell align="right">Betrag</TableCell>
                <TableCell>Verwendungszweck</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isPreviewing ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : exportableCount === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      Keine exportierbaren Buchungen in der aktuellen Auswahl.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                (previewData?.payments || []).map((payment) => (
                  <TableRow key={String(payment?.registrationId || payment?.mandateId || "")}>
                    <TableCell>{payment?.registrationNumber || "—"}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={
                          payment?.source === "public"
                            ? "Öffentlich"
                            : payment?.source === "member_portal"
                              ? "Mitgliederportal"
                              : payment?.source === "admin_manual"
                                ? "Admin"
                                : "—"
                        }
                      />
                    </TableCell>
                    <TableCell>{payment?.debtorName || "—"}</TableCell>
                    <TableCell>{maskIban(payment?.iban)}</TableCell>
                    <TableCell align="right">{formatCurrency(payment?.amount)}</TableCell>
                    <TableCell>{payment?.remittance || "—"}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isExporting}>
          Abbrechen
        </Button>
        <Button
          variant="contained"
          onClick={handleExport}
          disabled={
            isExporting ||
            isPreviewing ||
            !eventId ||
            !selectedAccountId ||
            exportableCount <= 0 ||
            !collectionDate
          }
          startIcon={
            isExporting ? (
              <CircularProgress color="inherit" size={16} />
            ) : (
              <Icon icon="mdi:file-download-outline" />
            )
          }
        >
          SEPA-Datei erzeugen und Status setzen
        </Button>
      </DialogActions>
    </Dialog>
  );
}

EventRegistrationsSepaDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  event: PropTypes.object,
  selectedRegistrationIds: PropTypes.array,
  onExported: PropTypes.func,
};

EventRegistrationsSepaDialog.defaultProps = {
  onClose: undefined,
  event: null,
  selectedRegistrationIds: [],
  onExported: undefined,
};
